<!-- Image generation button -->
<template>
  <div
    v-if="accessAdmin"
    class="recipe-imagegen-container"
    :class="{ 'prompt-active': promptModeActive }"
  >
    <!-- Initial Refresh Button -->
    <button
      v-if="recipeId && recipeName && !promptModeActive && !isRegenerating"
      @click="activatePromptMode"
      class="btn btn-like"
      title="Улучшить изображение"
    >
      <i class="bi bi-arrow-clockwise"></i>
    </button>

    <!-- Prompt Input Mode -->
    <div
      v-if="promptModeActive && !isRegenerating"
      class="prompt-input-section d-flex align-items-center"
    >
      <textarea
        v-model="additionalConditions"
        placeholder="Доп. условия (необязательно)"
        class="form-control form-control-sm me-2 flex-grow-1"
        @keyup.enter="performRegenerateImage"
        ref="additionalConditionsInput"
        rows="2"
      />
      <button
        @click="performRegenerateImage"
        class="btn btn-sm btn-icon"
        title="Сгенерировать"
      >
        <i class="bi bi-magic"></i>
      </button>
      <button
        @click="deactivatePromptMode"
        class="btn btn-sm btn-icon ms-1"
        title="Отмена"
      >
        <i class="bi bi-x-lg"></i>
      </button>
    </div>

    <!-- Spinner (shown when isRegenerating is true) -->
    <div v-if="isRegenerating" class="spinner-container">
      <span
        class="spinner-border spinner-border-sm"
        role="status"
        aria-hidden="true"
      ></span>
    </div>
  </div>
</template>

<script>
export default {
  name: "RecipeImagegen",
  props: {
    recipeId: {
      type: [String, Number],
      required: true,
    },
    recipeName: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      accessAdmin: false,
      isRegenerating: false,
      promptModeActive: false,
      additionalConditions: "",
    };
  },

  mounted() {
    this.accessAdmin = this.$globalState.accessAdmin;
  },

  watch: {
    "$globalState.accessAdmin"(newAccessAdmin) {
      this.accessAdmin = newAccessAdmin;
    },
  },

  methods: {
    activatePromptMode() {
      this.promptModeActive = true;
      this.$emit("prompt-mode-toggled", true);
      this.$nextTick(() => {
        if (this.$refs.additionalConditionsInput) {
          this.$refs.additionalConditionsInput.focus();
        }
      });
    },
    deactivatePromptMode() {
      this.promptModeActive = false;
      this.additionalConditions = "";
      this.$emit("prompt-mode-toggled", false);
    },
    async performRegenerateImage() {
      console.log(
        `RecipeImagegen: Regenerate image requested for recipe ID ${this.recipeId} (${this.recipeName}) with conditions: '${this.additionalConditions}'`
      );

      this.isRegenerating = true;
      // Inform the parent component that the prompt input UI is no longer active,
      // so it can restore the layout (e.g., show elements to the left).
      // The internal state `this.promptModeActive` will be fully reset in the finally block.
      this.$emit("prompt-mode-toggled", false);
      try {
        // Construct the command with recipeName as a path parameter
        const command = "image.generate/" + this.recipeName;
        const response = await this.sendCmd(
          command,
          this.additionalConditions,
          120000
        );

        console.log(
          "RecipeImagegen: Image regeneration command sent. Response:",
          response
        );

        this.$emit("image-regenerated", this.recipeId, this.recipeName);
      } catch (error) {
        console.error(
          "RecipeImagegen: Error sending image regeneration command:",
          error
        );
        this.$emit("regeneration-error", error, this.recipeId);
      } finally {
        this.isRegenerating = false;
        this.promptModeActive = false; // Deactivate prompt mode after attempt
        this.$emit("prompt-mode-toggled", false); // Ensure parent knows prompt mode is off
        this.additionalConditions = ""; // Clear conditions
      }
    },
  },
};
</script>

<style scoped>
.recipe-imagegen-container {
  /* Ensures the container itself doesn't add extra unexpected space if it's empty,
     but allows content to define its height and margins. */
  display: inline-block; /* Or flex, depending on desired alignment with siblings */
  vertical-align: middle; /* Helps align with other inline-block elements like RecipeLike */
}
.recipe-imagegen-container.prompt-active {
  display: block; /* Change display to allow full width */
  width: 100%; /* Take full available width */
}

.btn-like {
  background: none;
  border: none;
  margin-bottom: 12px;
  font-size: 1.5rem;
  padding: 0;
  line-height: 1;
}

.prompt-input-section {
  margin-bottom: 12px; /* Match original btn-like margin for consistency */
}

.btn-icon {
  background: none;
  border: none;
  padding: 0.25rem 0.4rem; /* Compact padding */
  font-size: 1.2rem; /* Icon size */
  line-height: 1;
  color: #6c757d; /* Bootstrap secondary color */
}
.btn-icon:hover {
  color: #0d6efd; /* Bootstrap primary color */
}

.spinner-container {
  font-size: 1.5rem; /* Matches .btn-like font-size for consistent spinner size */
  line-height: 1; /* Matches .btn-like line-height */
  margin-bottom: 12px; /* Matches .btn-like margin */
  display: inline-flex; /* To keep it compact like a button */
  align-items: center;
  justify-content: center;
  min-width: 24px; /* Approximate width of the icon button */
  min-height: 24px; /* Approximate height of the icon button */
}

.spinner-border-sm {
  width: 1em;
  height: 1em;
}
</style>
